import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CacheBucket, HttpCacheManager, withCache } from '@ngneat/cashew';
import { Utils } from '@serasa/shared/utils/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from "rxjs/operators";

import { IMyAudit } from '@serasa/core/pages/actions-history/models/deleted-user.interface';
import { environment } from '../../../../../environments/environment';
import { UserMe } from '../../recertification/interfaces/user-me.interface';
import { AccountResponse } from '../interfaces/account-response.interface';
import { CloneLogonRequest } from '../interfaces/clone-logon.interface';
import { Logon } from '../interfaces/logon.interface';
import { IUserAccountUpdatePayload } from '../interfaces/payload-update-user-account.interface';
import { IUserProfileUpdatePayload } from "../interfaces/payload-update-user-profile.interface";
import { ResetUser } from '../interfaces/reset-user.interface';
import { User } from '../interfaces/user.interface';

@Injectable({ providedIn: 'root' })
export class UserService {

  public currentUser$ = new BehaviorSubject<Partial<User>>({});
  public updated$ = new BehaviorSubject<Partial<boolean>>(false);
  public accessToken$ = new BehaviorSubject('');

  private logonsBucket = new CacheBucket();

  constructor(
    private readonly _http: HttpClient,
    public bucket: HttpCacheManager,
    @Inject('CACHE_EXPIRATION') private cacheExpiration: number
  ) { }

  public resetPassword(user: ResetUser) {
    const headers = Utils.generateDefaultHeader();

    return this._http.post(
      `${environment.logonPortalBaseUrl}/user-identities/reset-password`,
      user,
      { headers }
    );
  }

  public getCloneableLogonSearchData(logon: string) {
    const headers = Utils.generateDefaultHeader();

    return this._http.get(
      `${environment.logonPortalBaseUrl}/my-logons/${logon}/clone`,
      {
        headers,
      }
    );
  }

  public cloneLogon(logon: CloneLogonRequest) {
    const headers = Utils.generateDefaultHeader();

    return this._http.post(
      `${environment.logonPortalBaseUrl}/my-logons/${logon?.logon}/clone`,
      logon,
      { headers }
    );
  }

  public getUserAccount(userId: string): Observable<Logon> {
    const headers = Utils.generateDefaultHeader();
    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `user-accounts-${userId}`,
      bucket: this.logonsBucket,
    };

    return this._http.get(
      `${environment.logonPortalBaseUrl}/user-accounts/${userId}`,
      {
        headers,
        context: withCache(cacheOptions),
      }
    );
  }

  public updateUserAccount(
    logon: string,
    userId: string,
    payload: IUserAccountUpdatePayload
  ): Observable<unknown> {

    const defaultHeader = Utils.generateDefaultHeader();
    const headers = defaultHeader.append('Invalidate-Cache', 'true');

    const params = new HttpParams({
      fromObject: {
        logon,
      },
    });

    return this._http.patch<unknown>(
      `${environment.logonPortalBaseUrl}/user-accounts/${userId}`,
      { ...payload },
      {
        headers,
        params,
      }
    );
  }

  public updateUserProfile(
    userId: string,
    profile: string,
  ): Observable<unknown> {
    const headers = Utils.generateDefaultHeader();

    const payload: IUserProfileUpdatePayload = { profile };

    return this._http.patch<unknown>(
      `${environment.logonPortalBaseUrl}/my-logons/${userId}/profile`,
      payload,
      {
        headers,
      },
    ).pipe(
      tap({
        next: (response: unknown) => {
          this.invalidateLogonsCache();
          this.updated$.next(true);
        },
        error: (error: unknown) => {
          console.error('UserService - Erro na requisição:', error);
        }
      })
    );
  }

  public getAllLogons(
    offset?: number,
    pageSize?: number
  ): Observable<AccountResponse> {
    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader();

    const params = new HttpParams({
      fromObject: {
        offset: offset ?? 0,
        limit: pageSize ?? 20,
      },
    });

    const cacheOptions = {
      strategy: 'implicit',
      ttl: this.cacheExpiration,
      key: `logons-${offset}`,
      bucket: this.logonsBucket,
    };

    return this._http.get<AccountResponse>(url, {
      headers,
      params,
      context: withCache(cacheOptions),
    });
  }

  public searchByLogon(logon: string): Observable<AccountResponse> {
    const shouldReset = logon?.trim();
    const params =
      shouldReset === ''
        ? {}
        : new HttpParams({
            fromObject: {
              logon,
            },
          });

    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `logons-${logon}`,
      bucket: this.logonsBucket,
    };

    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader().append('Invalidate-cache', 'true');

    return this._http.get<AccountResponse>(url, {
      headers,
      params,
      context: withCache(cacheOptions),
    });
  }

  public getUserMeInfo(): Observable<UserMe> {
    const headers = Utils.generateDefaultHeader();

    return this._http.get<UserMe>(
      `${environment.logonPortalBaseUrl}/user-accounts/me`,
      {
        headers,
      }
    );
  }

  public getMyAudits(params?: Map<string, string>): Observable<IMyAudit> {
    let url = `${environment.logonPortalBaseUrl}/my-audits`;
    let headers = Utils.generateDefaultHeader();

    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `logons-myAudits-${params?.get('offset')}`,
      bucket: this.logonsBucket,
    };

    if (params) {
      const queryString = Utils.paramsToString(params);
      url = `${url}?${queryString}`;
    }

    return this._http.get<IMyAudit>(url, {
      headers,
      context: withCache(cacheOptions),
    });
  }

  public invalidateLogonsCache(): void {
    this.bucket.delete(this.logonsBucket);
  }
}
